import React, { useState, useEffect } from 'react';
import { socket } from '../Containers/MainContainer';
import Skeleton from 'react-loading-skeleton';

// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Button Component
const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false }) => {
  const baseStyle = {
    borderRadius: '8px',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const variantStyles = {
    primary: { background: '#238636', borderColor: '#2ea043', color: '#ffffff', border: '1px solid #2ea043' },
    secondary: { background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d' },
    success: { background: '#238636', color: '#ffffff', border: '1px solid #2ea043' },
    danger: { background: '#da3633', color: '#ffffff', border: '1px solid #f85149' },
  };

  const sizeStyles = {
    sm: { padding: '6px 12px', fontSize: '12px' },
    md: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' },
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...variantStyles[variant], ...sizeStyles[size] }}
      className={className}
    >
      {children}
    </button>
  );
};

// Input Component
const Input = ({ type = "text", placeholder, value, onChange, className = "", id }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    className={className}
    style={{
      padding: '8px 16px',
      background: '#0d1117',
      border: '1px solid #30363d',
      borderRadius: '8px',
      color: '#e6edf3',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s ease',
      width: className.includes('w-20') ? '80px' : className.includes('w-full') ? '100%' : 'auto',
    }}
    onFocus={(e) => { e.target.style.borderColor = '#58a6ff'; }}
    onBlur={(e) => { e.target.style.borderColor = '#30363d'; }}
  />
);

// Card Component
const Card = ({ title, children, className = "", headerActions }) => (
  <div style={{
    background: '#161b22',
    borderRadius: '12px',
    border: '1px solid #21262d',
    overflow: 'hidden',
  }}>
    {title && (
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #21262d',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#e6edf3', margin: 0 }}>{title}</h3>
        {headerActions}
      </div>
    )}
    <div style={{ padding: '16px' }}>{children}</div>
  </div>
);

// Table Component
const Table = ({ headers, children, isLoading = false }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#161b22' }}>
          {headers.map((header, index) => (
            <th key={index} style={{
              padding: '12px 16px',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: 500,
              color: '#8b949e',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              borderBottom: '1px solid #21262d',
              whiteSpace: 'nowrap',
            }}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          Array.from({ length: 10 }, (_, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #21262d' }}>
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} style={{ padding: '16px' }}>
                  <Skeleton baseColor="#161b22" highlightColor="#21262d" style={{ width: '100%', height: '16px' }} />
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
      <Button variant="secondary" size="sm" onClick={() => requestNewList(1)} disabled={currentPage === 1}>
        First
      </Button>
      <Button variant="secondary" size="sm" onClick={() => requestNewList(currentPage - 1)} disabled={currentPage === 1}>
        Prev
      </Button>
      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
        >
          {page}
        </Button>
      ))}
      <Button variant="secondary" size="sm" onClick={() => requestNewList(currentPage + 1)} disabled={currentPage === pageNumber}>
        Next
      </Button>
      <Button variant="secondary" size="sm" onClick={() => requestNewList(pageNumber)} disabled={currentPage === pageNumber}>
        Last
      </Button>
    </div>
  );
};

// Main Component
const PlutoOfferDataTotal = () => {
  const [userProfile, setUserProfile] = useState({});
  const [earnings, setEarnings] = useState([]);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [jumpPage, setJumpPage] = useState('');

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getCurrentMonthNumber = () => {
    return new Date().getMonth();
  };

  const getMonthName = (offset) => {
    const monthIndex = getCurrentMonthNumber() - offset;
    return monthNames[monthIndex < 0 ? 12 + monthIndex : monthIndex];
  };

  useEffect(() => {
    socket.on('response_get_admin_offersdata', (data) => {
      setEarnings(data.withdrawals);
      setPageNumber(Math.ceil(data.withdrawalsLength / 10));
      setUserProfile(data);
      setLoading(false);
    });

    socket.emit('getOffersDataAdmin', {
      offers_like: username,
      page,
    });

    return () => {
      socket.off('response_get_admin_offersdata');
    };
  }, []);

  const handleSearch = (value) => {
    setUsername(value);
    setLoading(true);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setLoading(true);
    socket.emit('getOffersDataAdmin', {
      offers_like: username,
      page: newPage,
    });
  };

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpPage);
    if (pageNum && pageNum > 0 && pageNum <= pageNumber) {
      handlePageChange(pageNum);
      setJumpPage('');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117' }}>
      {/* Main Content */}
      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Search and Filters */}
        <Card title="Search Offers">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <Input
                placeholder="Search by offer name..."
                value={username}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full"
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setLoading(true);
                setEarnings([]);
                socket.emit('getOffersDataAdmin', {
                  offers_like: username.toLowerCase(),
                  page: 1,
                });
              }}
            >
              Search
            </Button>
            {username && (
              <Button variant="secondary" onClick={() => handleSearch('')}>
                Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Data Table */}
        <Card
          title="Suspect Offers"
          headerActions={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#8b949e' }}>
                {username && `Search: "${username}" \u2022 `}
                Page {page} of {pageNumber} ({earnings.length} items)
              </span>
            </div>
          }
        >
          <Table
            headers={[
              'Offer Name',
              'Wall',
              'Completed',
              'Total CB',
              getMonthName(1),
              getMonthName(2),
              getMonthName(3),
              getMonthName(4),
              getMonthName(5),
              getMonthName(6)
            ]}
            isLoading={loading && earnings.length === 0}
          >
            {earnings.map((earning, i) => (
              <tr key={earning.id || i} style={{ borderBottom: '1px solid #21262d', transition: 'background 0.15s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1c2129'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <td style={{ padding: '16px', fontSize: '14px', color: '#e6edf3', maxWidth: '250px' }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={earning.name}>
                    {earning.name}
                  </div>
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px' }}>
                  <span style={{
                    padding: '4px 10px',
                    background: '#21262d',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    color: '#c9d1d9',
                  }}>
                    {earning.wall}
                  </span>
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#3fb950', fontWeight: 500 }}>
                  {numberWithCommas(earning.count)}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: 500 }}>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    background: earning.chargebacks > 20
                      ? 'rgba(248,81,73,0.15)'
                      : earning.chargebacks > 10
                        ? 'rgba(249,115,22,0.15)'
                        : '#21262d',
                    color: earning.chargebacks > 20
                      ? '#f85149'
                      : earning.chargebacks > 10
                        ? '#f97316'
                        : '#c9d1d9',
                  }}>
                    {numberWithCommas(earning.chargebacks)}
                  </span>
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {earning.cbMonth1}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {earning.cbMonth2}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {earning.cbMonth3}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {earning.cbMonth4}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {earning.cbMonth5}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {earning.cbMonth6}
                </td>
              </tr>
            ))}
          </Table>

          {/* Pagination */}
          {pageNumber > 0 && (
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <PaginationPluto
                pageNumber={pageNumber}
                currentPage={page}
                requestNewList={handlePageChange}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Input
                  type="number"
                  placeholder="Page"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  className="w-20"
                  id="jumptototal"
                />
                <Button variant="secondary" size="sm" onClick={handleJumpToPage}>
                  Go
                </Button>
              </div>
            </div>
          )}

          {earnings.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#x1F50D;</div>
              <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#e6edf3', marginBottom: '8px' }}>No offers found</h3>
              <p style={{ color: '#8b949e', fontSize: '14px' }}>
                {username
                  ? `No offers found matching "${username}". Try adjusting your search term.`
                  : 'No suspect offers found. Try adjusting your search criteria.'
                }
              </p>
              {username && (
                <div style={{ marginTop: '16px' }}>
                  <Button variant="secondary" onClick={() => handleSearch('')}>
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default PlutoOfferDataTotal;
